import ListSubheader from '@material-ui/core/ListSubheader';
import { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete';
import React, { HTMLAttributes } from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

const LISTBOX_PADDING = 6;
const DEFAULT_ITEM_HEIGHT = 34;
const LIST_ITEM = 8;
const OVERSCANCOUNT = 5;

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const top =
    typeof style.top === 'number'
      ? style.top + LISTBOX_PADDING
      : LISTBOX_PADDING;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

interface GroupListboxComponentProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Height for list items. Can be a fixed number or a function for variable heights.
   * @example Fixed: itemHeight={32}
   * @example Variable: itemHeight={(index) => index < 2 ? 36 : 32}
   */
  itemHeight?: number | ((index: number) => number);
}

export const GroupListboxComponent = React.forwardRef<
  HTMLDivElement,
  GroupListboxComponentProps
>((props, ref) => {
  const { children, itemHeight = DEFAULT_ITEM_HEIGHT, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;

  // Resolves item height - supports both fixed number and dynamic function
  const getItemHeight = (index: number) => {
    if (typeof itemHeight === 'function') {
      return itemHeight(index);
    }
    return itemHeight;
  };

  // Returns height for a specific child element at given index
  const getChildSize = (child: React.ReactNode, index: number) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return getItemHeight(index);
    }

    return getItemHeight(index);
  };

  // Calculates total visible height of the listbox
  const getHeight = () => {
    // When more items than visible limit, only sum visible items
    if (itemCount > LIST_ITEM) {
      if (typeof itemHeight === 'function') {
        let sum = 0;
        for (let i = 0; i < LIST_ITEM; i++) {
          sum += getItemHeight(i);
        }
        return sum;
      }
      return LIST_ITEM * itemHeight;
    }
    // For fewer items, sum all heights
    let sum = 0;
    for (let i = 0; i < itemData.length; i++) {
      sum += getChildSize(itemData[i], i);
    }
    return sum;
  };

  const gridRef = useResetCache(itemCount);
  return (
    <div
      ref={ref}
      className="[&_.MuiListSubheader-root]:text-gray-small [&_.MuiListSubheader-root]:text-[12px] [&_.MuiListSubheader-root]:font-medium [&_.MuiListSubheader-root]:leading-[19px] [&_.MuiAutocomplete-option]:min-h-auto"
    >
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight()}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index], index)}
          overscanCount={OVERSCANCOUNT}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});
type ListboxComponentProps = HTMLAttributes<HTMLDivElement>;
export const ListboxComponent = (props: ListboxComponentProps) => {
  return <GroupListboxComponent {...props} />;
};
export const RenderGroup = (params: AutocompleteRenderGroupParams) => [
  params.group ? (
    <ListSubheader
      key={params.key}
      component="div"
      className="flex items-center"
    >
      {params.group}
    </ListSubheader>
  ) : null,

  params.children,
];
